import { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import SubAbaCorretorFiltro from "../../Filtros/SubAbaCorretorFiltro";
import TabelaGerentes from "./TabelaGerentes";
import {
  getGerentes,
  updateGerente,
  createGerenteJustificativa,
  createGerente,
  getImobiliarias,
  deleteGerente,
} from "../../AssistenteVendasGestaoEndPoints";
import ModalEdicaoGerente from "../../Modals/ModalEdicaoGerente";
import ModalCadastroGerente from "../../Modals/ModalCadastroGerente";
import ModalExclusaoGerente from "../../Modals/ModalExclusaoGerente";
import { jwtDecode } from "jwt-decode";
import { getReadonlyFlag } from "../../../../../utils/getRoleToken";

const GerenteAba = ({
  imobiliariasDisponiveis = [],
  regioesDisponiveis = [],
  onContactClick = () => {},
}) => {
  const [clienteId, setClienteId] = useState(null);
  const [usuarioNome, setUsuarioNome] = useState("");
  const [usuarioId, setUsuarioId] = useState(null);
  const [nomeFilter, setNomeFilter] = useState("");
  const [telefoneFilter, setTelefoneFilter] = useState("");
  const [imobiliariaFilter, setImobiliariaFilter] = useState("");
  const [regiaoFilter, setRegiaoFilter] = useState("");
  const [gerentesBase, setGerentesBase] = useState([]);
  const [gerentesFiltrados, setGerentesFiltrados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalEdicaoAberto, setModalEdicaoAberto] = useState(false);
  const [gerenteSelecionado, setGerenteSelecionado] = useState(null);
  const [salvandoGerente, setSalvandoGerente] = useState(false);
  const [edicaoSuccess, setEdicaoSuccess] = useState(false);
  const [edicaoError, setEdicaoError] = useState("");
  const [modalCadastroOpen, setModalCadastroOpen] = useState(false);
  const [cadastroSaving, setCadastroSaving] = useState(false);
  const [cadastroSuccess, setCadastroSuccess] = useState(false);
  const [cadastroError, setCadastroError] = useState("");
  const [imobiliariasCadastro, setImobiliariasCadastro] = useState([]);
  const [carregandoImobiliarias, setCarregandoImobiliarias] = useState(false);
  const [modalExclusaoOpen, setModalExclusaoOpen] = useState(false);
  const [gerenteParaExcluir, setGerenteParaExcluir] = useState(null);
  const [excluindoGerente, setExcluindoGerente] = useState(false);
  const [exclusaoSuccess, setExclusaoSuccess] = useState(false);
  const [exclusaoError, setExclusaoError] = useState("");
  const podeEditar = getReadonlyFlag() !== true;

  const sanitizeNumber = (value) => (value || "").replace(/\D/g, "");
  const parseFirstImobiliariaId = (idsImobiliarias) => {
    if (typeof idsImobiliarias !== "string") return null;
    const raw = idsImobiliarias
      .split("|")
      .map((item) => item.trim())
      .find((item) => item.length > 0);
    if (!raw) return null;
    const parsed = Number(raw.replace(/\D/g, ""));
    return Number.isNaN(parsed) ? null : parsed;
  };

  const filtrarGerentes = (listaBase = gerentesBase) => {
    const normalize = (value) => (value || "").toString().toLowerCase();
    const sanitizePhone = (value) => (value || "").toString().replace(/\D/g, "");
    const telefoneBusca = sanitizePhone(telefoneFilter);

    return listaBase.filter((gerente) => {
      const nomeMatch = nomeFilter
        ? normalize(gerente.nome).includes(normalize(nomeFilter))
        : true;

      const telefonesConcatenados = `${sanitizePhone(
        gerente.telefone
      )}${sanitizePhone(gerente.celular)}`;
      const telefoneMatch = telefoneFilter
        ? telefonesConcatenados.includes(telefoneBusca)
        : true;

      const imobiliariaMatch = imobiliariaFilter
        ? normalize(gerente.imobiliaria).includes(normalize(imobiliariaFilter))
        : true;

      const regiaoMatch = regiaoFilter
        ? normalize(gerente.regiao).includes(normalize(regiaoFilter))
        : true;

      return nomeMatch && telefoneMatch && imobiliariaMatch && regiaoMatch;
    });
  };

  const extrairMensagemErro = (error) => {
    const resposta = error?.response?.data;

    if (resposta) {
      if (typeof resposta === "string") {
        const conteudoEntreColchetes = resposta.match(/\[\s*([\s\S]*?)\s*\]/);
        if (conteudoEntreColchetes?.[1]?.trim()) {
          return conteudoEntreColchetes[1].trim();
        }
        if (resposta.trim()) return resposta.trim();
      }

      if (typeof resposta === "object") {
        const camposPossiveis = ["message", "mensagem", "error"];
        for (const campo of camposPossiveis) {
          const valorCampo = resposta[campo];
          if (typeof valorCampo === "string" && valorCampo.trim()) {
            return valorCampo.trim();
          }
        }
      }
    }

    if (typeof error?.message === "string" && error.message.trim()) {
      return error.message.trim();
    }

    return "";
  };

  const fetchGerentes = async (idClienteParam) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) return;

      const decodedToken = jwtDecode(token);
      const idClienteLocal = idClienteParam ?? decodedToken.id_cliente;
      setClienteId(idClienteLocal);
      setUsuarioNome(decodedToken.nome || decodedToken.nomeUsuario || "Usuario");
      setUsuarioId(decodedToken.id_usuario || decodedToken.idUsuario || null);

      const dados = await getGerentes(idClienteLocal);

      const gerentesMapeados = (dados || []).map((gerente, idx) => {
        const telefoneSanitizado = sanitizeNumber(
          gerente.telefone || gerente.celular || ""
        );
        const chaveBase =
          telefoneSanitizado || `${gerente.nomeGerente || "gerente"}-${idx}`;
        const idsImobiliarias =
          gerente.idsImobiliarias ??
          gerente.ids_imobiliarias ??
          gerente.idsImobiliaria ??
          gerente.ids_imobiliaria ??
          "";
        const idImobiliaria =
          gerente.idImobiliaria ??
          gerente.id_imobiliaria ??
          gerente.imobiliarias?.[0]?.idImobiliaria ??
          gerente.imobiliaria?.idImobiliaria ??
          parseFirstImobiliariaId(idsImobiliarias);

        return {
          uniqueKey: String(chaveBase),
          matchKey: String(chaveBase),
          nome: gerente.nomeGerente,
          telefone: gerente.telefone || gerente.celular,
          celular: gerente.celular,
          imobiliaria: gerente.nomeImobiliarias,
          regiao: gerente.nomeRegioes,
          email: gerente.email,
          idClienteAipim: gerente.idClienteAipim,
          idImobiliaria,
          idsImobiliarias,
        };
      });

      setGerentesBase(gerentesMapeados);
      setGerentesFiltrados(filtrarGerentes(gerentesMapeados));
    } catch (error) {
      console.error("Erro ao carregar gerentes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGerentes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleApplyFilters = () => {
    setLoading(true);
    setTimeout(() => {
      setGerentesFiltrados(filtrarGerentes());
      setLoading(false);
    }, 500);
  };

  const handleClearFilters = () => {
    setNomeFilter("");
    setTelefoneFilter("");
    setImobiliariaFilter("");
    setRegiaoFilter("");
    setLoading(true);
    setTimeout(() => {
      setGerentesFiltrados(gerentesBase);
      setLoading(false);
    }, 500);
  };

  const imobiliariasDisponiveisMemo = useMemo(() => {
    const todasImobiliarias = gerentesBase.flatMap((gerente) =>
      (gerente.imobiliaria || "")
        .split("|")
        .map((item) => item.trim())
        .filter(Boolean)
    );

    return Array.from(new Set(todasImobiliarias));
  }, [gerentesBase]);

  const regioesDisponiveisMemo = useMemo(() => {
    const todasRegioes = gerentesBase.flatMap((gerente) =>
      (gerente.regiao || "")
        .split("|")
        .map((item) => item.trim())
        .filter(Boolean)
    );

    return Array.from(new Set(todasRegioes));
  }, [gerentesBase]);

  const handleOpenEdicao = (gerente) => {
    if (!podeEditar) return;
    setGerenteSelecionado(gerente);
    setEdicaoError("");
    setEdicaoSuccess(false);
    setModalEdicaoAberto(true);
  };

  const handleCloseEdicao = () => {
    setModalEdicaoAberto(false);
    setGerenteSelecionado(null);
    setEdicaoError("");
    setEdicaoSuccess(false);
  };

  const resolveUsuarioJustificativa = () => {
    if (usuarioId && usuarioNome) {
      return { idUsuario: usuarioId, nomeUsuario: usuarioNome };
    }

    const token = localStorage.getItem("jwtToken");
    if (!token) return { idUsuario: null, nomeUsuario: null };

    try {
      const decoded = jwtDecode(token);
      const idUsuario = decoded?.id_usuario ?? decoded?.idUsuario ?? null;
      const nomeUsuario = decoded?.nome ?? decoded?.nomeUsuario ?? "Usuario";

      if (!usuarioId && idUsuario) setUsuarioId(idUsuario);
      if (!usuarioNome && nomeUsuario) setUsuarioNome(nomeUsuario);

      return { idUsuario, nomeUsuario };
    } catch (error) {
      console.error("Erro ao decodificar usuario do JWT:", error);
      return { idUsuario: null, nomeUsuario: null };
    }
  };

  const fetchImobiliarias = async (idClienteParam) => {
    setCarregandoImobiliarias(true);
    try {
      let idClienteLocal = idClienteParam ?? clienteId;
      if (!idClienteLocal) {
        const token = localStorage.getItem("jwtToken");
        if (token) {
          const decodedToken = jwtDecode(token);
          idClienteLocal = decodedToken.id_cliente;
          setClienteId(idClienteLocal);
          setUsuarioNome(decodedToken.nome || decodedToken.nomeUsuario || "Usuario");
          setUsuarioId(decodedToken.id_usuario || decodedToken.idUsuario || null);
        }
      }

      if (!idClienteLocal) {
        setImobiliariasCadastro([]);
        return;
      }

      const dados = await getImobiliarias(idClienteLocal);
      const imobiliariasMapeadas = (dados || [])
        .map((imobiliaria) => ({
          id:
            imobiliaria.idImobiliaria ??
            imobiliaria.id ??
            imobiliaria.id_imobiliaria ??
            null,
          nome:
            imobiliaria.nomeImobiliaria ||
            imobiliaria.nome ||
            imobiliaria.imobiliaria ||
            "",
        }))
        .filter((item) => item.id != null);

      setImobiliariasCadastro(imobiliariasMapeadas);
    } catch (error) {
      console.error("Erro ao carregar imobiliarias:", error);
      setImobiliariasCadastro([]);
    } finally {
      setCarregandoImobiliarias(false);
    }
  };

  const registrarJustificativaGerenteEdicao = async ({
    nomeAnterior,
    nomeAtual,
    celularAnterior,
    celularAtual,
  }) => {
    if (!clienteId || !usuarioId) return;

    const campos = [];
    const nomeMudou = (nomeAnterior || "").trim() !== (nomeAtual || "").trim();
    const celularAnteriorDigits = sanitizeNumber(celularAnterior);
    const celularAtualDigits = sanitizeNumber(celularAtual);
    const celularMudou = celularAnteriorDigits !== celularAtualDigits;

    if (nomeMudou) {
      campos.push({
        label: "Nome",
        anterior: nomeAnterior || "-",
        novo: nomeAtual || "-",
      });
    }

    if (celularMudou) {
      campos.push({
        label: "Celular",
        anterior: celularAnterior || "-",
        novo: celularAtual || "-",
      });
    }

    if (campos.length === 0) return;

    const valorAnterior = campos
      .map((campo) => `${campo.label}: ${campo.anterior}`)
      .join(" | ");
    const valorNovo = campos
      .map((campo) => `${campo.label}: ${campo.novo}`)
      .join(" | ");
    const justificativaTexto = `${usuarioNome || "Usuario"} | [EDITOU] | Gerente: ${
      nomeAnterior || nomeAtual || "-"
    }`;

    try {
      await createGerenteJustificativa({
        idClienteAipim: clienteId,
        idUsuario: usuarioId,
        valorAnterior,
        valorNovo,
        justificativa: justificativaTexto,
      });
    } catch (error) {
      console.error("Erro ao registrar justificativa do gerente:", error);
    }
  };

  const handleConfirmarEdicao = async (payload) => {
    if (!clienteId || !gerenteSelecionado) return;

    const celularOriginalSanitizado = sanitizeNumber(
      gerenteSelecionado.celular ||
        gerenteSelecionado.telefone ||
        payload.celularAtual ||
        ""
    );
    const novoCelularSanitizado = sanitizeNumber(payload.novoCelular);
    const celularParaEnviar =
      novoCelularSanitizado || celularOriginalSanitizado;

    setSalvandoGerente(true);
    setEdicaoError("");
    setEdicaoSuccess(false);

    try {
      await updateGerente({
        idCliente: clienteId,
        celularAtual: celularOriginalSanitizado,
        novoCelular: celularParaEnviar,
        novoNome: payload.novoNome,
      });

      await registrarJustificativaGerenteEdicao({
        nomeAnterior: gerenteSelecionado.nome,
        nomeAtual: payload.novoNome,
        celularAnterior: celularOriginalSanitizado,
        celularAtual: celularParaEnviar,
      });

      await fetchGerentes(clienteId);
      setEdicaoSuccess(true);
    } catch (error) {
      console.error("Erro ao atualizar gerente:", error);
      const mensagemBackend = extrairMensagemErro(error);
      setEdicaoError(
        mensagemBackend ||
          "Nao foi possivel salvar as alteracoes. Tente novamente."
      );
    } finally {
      setSalvandoGerente(false);
    }
  };

  const handleOpenCadastro = async () => {
    if (!podeEditar) return;
    setCadastroError("");
    setCadastroSuccess(false);
    setModalCadastroOpen(true);

    if (imobiliariasCadastro.length === 0) {
      await fetchImobiliarias();
    }
  };

  const handleCloseCadastro = () => {
    if (cadastroSaving) return;
    setModalCadastroOpen(false);
    setCadastroError("");
    setCadastroSuccess(false);
  };

  const handleOpenExclusao = (gerente) => {
    if (!podeEditar) return;
    setGerenteParaExcluir(gerente);
    setExclusaoError("");
    setExclusaoSuccess(false);
    setModalExclusaoOpen(true);
  };

  const handleCloseExclusao = () => {
    if (excluindoGerente) return;
    setModalExclusaoOpen(false);
    setGerenteParaExcluir(null);
    setExcluindoGerente(false);
    setExclusaoError("");
    setExclusaoSuccess(false);
  };

  const registrarJustificativaGerenteCadastro = async ({
    nome,
    celular,
    imobiliaria,
  }) => {
    const idClienteAipim = clienteId;
    if (!idClienteAipim) return;

    const { idUsuario, nomeUsuario } = resolveUsuarioJustificativa();
    if (!idUsuario) return;

    const campos = [
      { label: "Nome", valor: nome },
      { label: "Celular", valor: celular },
      { label: "Imobiliaria", valor: imobiliaria },
    ].filter((campo) => campo.valor && campo.valor.toString().trim());

    const valorNovo = campos.map((campo) => `${campo.label}: ${campo.valor}`).join(" | ");
    const justificativaTexto = `${nomeUsuario || "Usuario"} | [CADASTROU] | Gerente: ${nome || "-"}`;

    try {
      await createGerenteJustificativa({
        idClienteAipim,
        idUsuario,
        valorAnterior: "",
        valorNovo,
        justificativa: justificativaTexto,
      });
    } catch (error) {
      console.error("Erro ao registrar justificativa de cadastro:", error);
    }
  };

  const registrarJustificativaGerenteExclusao = async (
    gerente,
    justificativaTexto,
    idClienteAipimParam
  ) => {
    const idClienteAipim = idClienteAipimParam ?? clienteId ?? gerente?.idClienteAipim;
    if (!idClienteAipim) return;

    const { idUsuario, nomeUsuario } = resolveUsuarioJustificativa();
    if (!idUsuario) return;

    const nomeGerente = (gerente?.nome || "-").toString();
    const celular = gerente?.celular || gerente?.telefone || "";
    const imobiliaria = gerente?.imobiliaria || "";

    const campos = [
      { label: "Nome", valor: nomeGerente },
      { label: "Celular", valor: celular },
      { label: "Imobiliaria", valor: imobiliaria },
    ].filter((campo) => campo.valor && campo.valor.toString().trim());

    const valorAnterior = campos
      .map((campo) => `${campo.label}: ${campo.valor}`)
      .join(" | ");

    const justificativaFinal = `${nomeUsuario || "Usuario"} | [EXCLUIU] | Gerente: ${nomeGerente} | ${justificativaTexto}`;

    try {
      await createGerenteJustificativa({
        idClienteAipim,
        idUsuario,
        valorAnterior,
        valorNovo: "",
        justificativa: justificativaFinal,
      });
    } catch (error) {
      console.error("Erro ao registrar justificativa de exclusão:", error);
    }
  };

  const handleConfirmarExclusao = async (justificativaTexto) => {
    let idClienteLocal = clienteId;

    if (!idClienteLocal) {
      const token = localStorage.getItem("jwtToken");
      if (token) {
        const decodedToken = jwtDecode(token);
        idClienteLocal = decodedToken.id_cliente;
        setClienteId(idClienteLocal);
      }
    }

    if (!idClienteLocal || !gerenteParaExcluir) return;

    const idImobiliariaRaw = gerenteParaExcluir.idImobiliaria;
    const idImobiliaria = Number(
      (idImobiliariaRaw ?? "").toString().replace(/\D/g, "")
    );
    if (!idImobiliaria || Number.isNaN(idImobiliaria)) {
      setExclusaoError(
        "Nao foi possivel identificar a imobiliaria do gerente para exclusao."
      );
      return;
    }

    setExcluindoGerente(true);
    setExclusaoError("");
    setExclusaoSuccess(false);

    try {
      await deleteGerente({
        idCliente: idClienteLocal,
        idImobiliaria,
        celular: gerenteParaExcluir.celular || gerenteParaExcluir.telefone || "",
      });

      await registrarJustificativaGerenteExclusao(
        gerenteParaExcluir,
        justificativaTexto,
        idClienteLocal
      );

      await fetchGerentes(idClienteLocal);
      setExclusaoSuccess(true);

      setTimeout(() => {
        handleCloseExclusao();
      }, 1200);
    } catch (error) {
      console.error("Erro ao excluir gerente:", error);
      const mensagemBackend = extrairMensagemErro(error);
      setExclusaoError(
        mensagemBackend ||
          "Nao foi possivel excluir o gerente. Tente novamente."
      );
    } finally {
      setExcluindoGerente(false);
    }
  };

  const handleConfirmarCadastro = async ({ nome, celular, idImobiliaria }) => {
    let idClienteLocal = clienteId;

    if (!idClienteLocal) {
      const token = localStorage.getItem("jwtToken");
      if (token) {
        const decodedToken = jwtDecode(token);
        idClienteLocal = decodedToken.id_cliente;
        setClienteId(idClienteLocal);
        setUsuarioNome(decodedToken.nome || decodedToken.nomeUsuario || "Usuario");
        setUsuarioId(decodedToken.id_usuario || decodedToken.idUsuario || null);
      }
    }

    if (!idClienteLocal) {
      setCadastroError("Nao foi possivel identificar o cliente para cadastro.");
      return;
    }

    setCadastroSaving(true);
    setCadastroError("");
    setCadastroSuccess(false);

    try {
      await createGerente({
        idCliente: idClienteLocal,
        idImobiliaria,
        nome,
        celular,
      });

      const imobiliariaSelecionada = imobiliariasCadastro.find(
        (item) => String(item.id) === String(idImobiliaria)
      );

      await registrarJustificativaGerenteCadastro({
        nome,
        celular,
        imobiliaria: imobiliariaSelecionada?.nome || "",
      });

      await fetchGerentes(idClienteLocal);
      setCadastroSuccess(true);
    } catch (error) {
      console.error("Erro ao cadastrar gerente:", error);
      const mensagemBackend = extrairMensagemErro(error);
      setCadastroError(
        mensagemBackend ||
          "Nao foi possivel cadastrar o gerente. Tente novamente."
      );
    } finally {
      setCadastroSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <SubAbaCorretorFiltro
        nomeFilter={nomeFilter}
        setNomeFilter={setNomeFilter}
        telefoneFilter={telefoneFilter}
        setTelefoneFilter={setTelefoneFilter}
        imobiliariaFilter={imobiliariaFilter}
        setImobiliariaFilter={setImobiliariaFilter}
        regiaoFilter={regiaoFilter}
        setRegiaoFilter={setRegiaoFilter}
        imobiliariasDisponiveis={imobiliariasDisponiveisMemo}
        regioesDisponiveis={regioesDisponiveisMemo}
        handleApplyFilters={handleApplyFilters}
        handleClearFilters={handleClearFilters}
        onOpenCadastro={handleOpenCadastro}
        showCadastro={podeEditar}
        cadastroLabel="Cadastrar novo Gerente"
      />

      <TabelaGerentes
        gerentes={gerentesFiltrados}
        loading={loading}
        onEdit={handleOpenEdicao}
        onTelefoneClick={onContactClick}
        onDelete={handleOpenExclusao}
        showEdit={podeEditar}
        showDelete={podeEditar}
      />

      <ModalCadastroGerente
        open={modalCadastroOpen}
        onClose={handleCloseCadastro}
        onConfirm={handleConfirmarCadastro}
        saving={cadastroSaving}
        success={cadastroSuccess}
        errorMessage={cadastroError}
        imobiliarias={imobiliariasCadastro}
        loadingImobiliarias={carregandoImobiliarias}
      />

      <ModalEdicaoGerente
        open={modalEdicaoAberto}
        onClose={handleCloseEdicao}
        gerente={gerenteSelecionado}
        usuarioNome={usuarioNome}
        saving={salvandoGerente}
        success={edicaoSuccess}
        errorMessage={edicaoError}
        onConfirm={handleConfirmarEdicao}
      />

      <ModalExclusaoGerente
        open={modalExclusaoOpen}
        onClose={handleCloseExclusao}
        gerente={gerenteParaExcluir}
        usuarioNome={usuarioNome}
        loading={excluindoGerente}
        success={exclusaoSuccess}
        errorMessage={exclusaoError}
        onConfirm={handleConfirmarExclusao}
      />
    </Box>
  );
};

export default GerenteAba;
